import AppObj from 'model/appConfigObj';
import { TaskInitParams } from 'model/taskObj';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faFilter,
  faFolder,
  faFolderPlus,
  faGrip,
  faHouse,
  faList,
  faGear,
  faRotateRight,
  faSort,
  faStar,
  faTag,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import fileBrowserApi, { DsEntry } from './api';
import fileClipboardService, { FileClipboardPayload } from 'service/fileClipboardService';
import { effectiveHomePath, hasChosenHome, preferenceForApp } from 'model/userSession';
import { desktopAppTaskContex } from 'screen/desktop';
import {
  DS_CHANGED_EVENT,
  snapshotDataTransfer,
  stashUploadDrop,
  takeUploadDrop,
} from 'screen/apps/fileUploader/api';
import { isImageFile, isNativeAudioFile, isNativeVideoFile, isPdfFile, isRasterImageFile, isVideoFile } from 'util/openableFile';
import { resolveEntryIcon } from './iconHandler';
import './fileBrowser.css';

/**
 * 檔案瀏覽器小程式（minapp）
 *
 * 檔案就這四個，不要再拆 class／檔案：
 * - index.tsx      UI + 導覽／框選／縮圖
 * - api.ts         axios calls（共用 apiClient）
 * - iconHandler.ts 副檔名對圖示
 * - fileBrowser.css
 *
 * 偏好（檢視模式、排序、圖示大小、書籤）寫入桌面 session，debounce 後上傳。
 */

/** 桌面啟動用：app id、預設視窗大小、工作列圖示。 */
export const fileBrowserConfig = {
  appID: 'fileBrowser',
  showName: 'FIle Browser',
  onList: true,
  icon: require('./images/fileManager.png'),
  maxHead: 5,
  req: {
    minSize: {
      width: 680,
      height: 440,
    },
    defSize: 'AUTO' as const,
  },
};

// ——— 檢視／排序偏好 ———

type ViewMode = 'icons' | 'list';
type SortKey = 'name' | 'size' | 'modified' | 'created';

const ICON_SIZE_MIN = 32;
const ICON_SIZE_MAX = 128;
const ICON_SIZE_DEFAULT = 56;

type Userpreference = {
  viewMode: ViewMode;
  showHiddenFiles: boolean;
  foldersFirst: boolean;
  sortKey: SortKey;
  sortAsc: boolean;
  iconSize: number;
  bookmark: string[];
};

const DEFAULT_APP_CONFIG: Userpreference = {
  viewMode: 'icons',
  showHiddenFiles: false,
  foldersFirst: true,
  sortKey: 'name',
  sortAsc: true,
  iconSize: ICON_SIZE_DEFAULT,
  bookmark: [],
};

/** 把偏好裡的圖示大小夾在合法範圍；壞值回退成目前預設 56。 */
function clampIconSize(n: number): number {
  if (!Number.isFinite(n)) {
    return ICON_SIZE_DEFAULT;
  }
  return Math.min(ICON_SIZE_MAX, Math.max(ICON_SIZE_MIN, Math.round(n)));
}

/** 右鍵選單座標；paths 為空代表點在空白處。 */
type CtxMenu = {
  x: number;
  y: number;
  paths: string[];
};

type Hist = { uri: string; scrollPX: number };

// ——— 路徑字串 ———

function basename(path: string): string {
  const cleaned = path.replace(/\/+$/, '');
  const idx = cleaned.lastIndexOf('/');
  return idx >= 0 ? cleaned.slice(idx + 1) : cleaned;
}

function parentPath(path: string): string | null {
  const cleaned = path.replace(/\/+$/, '') || '/';
  if (cleaned === '/' || cleaned === '') {
    return null;
  }
  const idx = cleaned.lastIndexOf('/');
  if (idx <= 0) {
    return '/';
  }
  return cleaned.slice(0, idx);
}

function joinPath(dir: string, name: string): string {
  if (dir.endsWith('/')) {
    return dir + name;
  }
  return dir + '/' + name;
}

function entryTags(e: DsEntry): string[] {
  return Array.isArray(e.tags) ? e.tags : [];
}

function entryHasAllTags(e: DsEntry, tags: string[]): boolean {
  if (tags.length === 0) {
    return true;
  }
  const have = new Set(entryTags(e));
  return tags.every((t) => have.has(t));
}

/** 書籤路徑去掉尾隨斜線；根目錄固定為 `/`。 */
function normalizeBookmarkPath(path: string): string {
  const cleaned = path.trim();
  if (!cleaned || cleaned === '/') {
    return cleaned ? '/' : '';
  }
  return cleaned.replace(/\/+$/, '');
}

/** 偏好裡的 bookmark 一律收成去重後的路徑陣列。 */
function normalizeBookmarks(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    let path = '';
    if (typeof item === 'string') {
      path = item;
    } else if (item && typeof item === 'object' && typeof (item as { path?: unknown }).path === 'string') {
      path = (item as { path: string }).path;
    }
    const n = normalizeBookmarkPath(path);
    if (!n || seen.has(n)) {
      continue;
    }
    seen.add(n);
    out.push(n);
  }
  return out;
}

function bookmarkLabel(path: string): string {
  if (path === '/') {
    return '/';
  }
  return basename(path) || path;
}

function loadFileBrowserPreference(
  user: Parameters<typeof preferenceForApp>[0]
): Userpreference {
  const merged = preferenceForApp(user, fileBrowserConfig.appID, DEFAULT_APP_CONFIG);
  return { ...merged, bookmark: normalizeBookmarks(merged.bookmark) };
}

function isFileDrag(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes('Files');
}

/** 貼上時若目標已存在，產生 `name (copy).ext`、`name (copy 2).ext`… */
function uniqueDestPath(dir: string, name: string, existing: Set<string>): string {
  let candidate = joinPath(dir, name);
  if (!existing.has(candidate)) {
    return candidate;
  }
  const dot = name.lastIndexOf('.');
  const hasExt = dot > 0 && !name.startsWith('.');
  const stem = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : '';
  let n = 1;
  while (true) {
    const nextName = `${stem} (copy${n > 1 ? ' ' + n : ''})${ext}`;
    candidate = joinPath(dir, nextName);
    if (!existing.has(candidate)) {
      return candidate;
    }
    n += 1;
  }
}

// ——— 列表顯示／排序 ———

function formatSize(bytes: number, isDir: boolean): string {
  if (isDir) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDate(epochMs?: number): string {
  if (!epochMs) {
    return '—';
  }
  const d = new Date(epochMs);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function compareEntries(a: DsEntry, b: DsEntry, key: SortKey): number {
  switch (key) {
    case 'size':
      return (a.size || 0) - (b.size || 0);
    case 'modified':
      return (a.lastModifiedEpochMs || 0) - (b.lastModifiedEpochMs || 0);
    case 'created':
      return (a.createdEpochMs || 0) - (b.createdEpochMs || 0);
    default:
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  }
}

// ——— 虛擬捲動／格線幾何（框選也共用同一套格子尺寸） ———

/** 視窗外多渲染幾列，捲動時比較不容易閃空。 */
const OVERSCAN = 3;

type GridMetrics = {
  cols: number;
  cellW: number;
  cellH: number;
  gapX: number;
  gapY: number;
  padL: number;
  padT: number;
  headerH: number;
};

const EMPTY_GRID: GridMetrics = {
  cols: 1,
  cellW: 0,
  cellH: 0,
  gapX: 0,
  gapY: 0,
  padL: 0,
  padT: 0,
  headerH: 0,
};

/** 把 CSS grid-template 的 px 軌道解析成數字陣列（含 repeat(n, Npx)）。 */
function parsePxTracks(value: string): number[] {
  if (!value || value === 'none') {
    return [];
  }
  const repeat = /^repeat\((\d+),\s*(.+)\)$/.exec(value.trim());
  if (repeat) {
    const n = Number(repeat[1]);
    const size = parseFloat(repeat[2]);
    if (n > 0 && Number.isFinite(size)) {
      return Array.from({ length: n }, () => size);
    }
  }
  return value.split(/\s+/).map(parseFloat).filter((n) => Number.isFinite(n) && n >= 0);
}

/** 從實際 DOM 讀出格子寬高／間距，供虛擬列表與框選命中計算。 */
function readGridMetrics(el: HTMLElement, mode: ViewMode): GridMetrics {
  const s = getComputedStyle(el);
  const colTracks = parsePxTracks(s.gridTemplateColumns);
  const rowTracks = parsePxTracks(s.gridTemplateRows);
  const gapX = parseFloat(s.columnGap) || 0;
  const gapY = parseFloat(s.rowGap) || 0;
  const padL = parseFloat(s.paddingLeft) || 0;
  const padT = parseFloat(s.paddingTop) || 0;
  if (mode === 'list') {
    return {
      cols: 1,
      cellW: colTracks[0] || el.clientWidth,
      cellH: parseFloat(s.getPropertyValue('--fb-list-row-h')) || rowTracks[1] || 0,
      gapX,
      gapY,
      padL,
      padT,
      headerH: parseFloat(s.getPropertyValue('--fb-list-header-h')) || rowTracks[0] || 0,
    };
  }
  return {
    cols: Math.max(1, colTracks.length),
    cellW: colTracks[0] || 0,
    cellH: parseFloat(s.getPropertyValue('--fb-cell-h')) || parseFloat(s.gridAutoRows) || rowTracks[0] || 0,
    gapX,
    gapY,
    padL,
    padT,
    headerH: 0,
  };
}

/** 依捲動位置算出目前該渲染的項目區間 [start, end)。 */
function visibleSlice(
  scrollTop: number,
  vpHeight: number,
  count: number,
  cols: number,
  cellH: number,
  gap: number,
  pad: number
): { start: number; end: number } {
  if (count === 0 || cellH <= 0 || cols <= 0) {
    return { start: 0, end: 0 };
  }
  const rows = Math.ceil(count / cols);
  const stride = cellH + gap;
  const startRow = Math.max(0, Math.floor((scrollTop - pad) / stride) - OVERSCAN);
  const endRow = Math.min(
    rows - 1,
    Math.ceil((scrollTop + vpHeight - pad) / stride) + OVERSCAN
  );
  return {
    start: startRow * cols,
    end: Math.min(count, (endRow + 1) * cols),
  };
}

/** 框選矩形與一排等寬格子的重疊列／欄範圍。 */
function overlappingTracks(
  boxStart: number,
  boxEnd: number,
  origin: number,
  cellSize: number,
  gap: number,
  count: number
): { start: number; end: number } | null {
  if (count <= 0) {
    return null;
  }
  const stride = cellSize + gap;
  const start = Math.max(0, Math.ceil((boxStart - origin - cellSize) / stride));
  const end = Math.min(count - 1, Math.floor((boxEnd - origin) / stride));
  if (start > end) {
    return null;
  }
  return { start, end };
}

function samePathSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  return b.every((p) => set.has(p));
}

// ——— 縮圖 ———

/** 一次跟後端要幾張縮圖，避免單次請求太大。 */
const THUMB_BATCH = 3;

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

/** 點陣圖、影片、音樂封面才跟後端要縮圖；SVG 用檔案圖示即可。 */
function isThumbnailCandidate(entry: DsEntry): boolean {
  return (
    !entry.directory &&
    (isVideoFile(entry.name) ||
      isNativeAudioFile(entry.name) ||
      (isImageFile(entry.name) && !entry.name.toLowerCase().endsWith('.svg')))
  );
}

function FileBrowser(props: { taskInitParms?: TaskInitParams[]; taskID?: string }) {
  const [desktopControll] = useContext(desktopAppTaskContex);

  // ——— DOM／暫存 ref（事件回呼裡讀最新值，避免過期 closure） ———
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const ctxMenuRef = useRef<HTMLDivElement>(null);
  const propRef = useRef<HTMLDivElement>(null);
  const tagEditorRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const requestedRef = useRef<Set<string>>(new Set());
  const thumbAbortRef = useRef<AbortController | null>(null);
  const thumbRunRef = useRef(0);
  const thumbByPathRef = useRef<Record<string, string>>({});
  const visibleImagePathsRef = useRef<Set<string>>(new Set());
  const thumbScheduleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thumbInFlightRef = useRef(false);
  const queueThumbnailFetchRef = useRef<((delayMs?: number) => void) | null>(null);
  const refreshGenRef = useRef(0);
  const pendingScrollRef = useRef<number | null>(0);
  const visibleEntriesRef = useRef<DsEntry[]>([]);
  const viewModeRef = useRef<ViewMode>('icons');
  const gridMetricsRef = useRef<GridMetrics>(EMPTY_GRID);
  const dragRef = useRef<{
    active: boolean;
    additive: boolean;
    startX: number;
    startY: number;
    startScrollLeft: number;
    startScrollTop: number;
    lastX: number;
    lastY: number;
    originSelected: string[];
  } | null>(null);
  const dropDepthRef = useRef(0);

  // ——— UI 狀態 ———
  const [bootReady, setBootReady] = useState(false);
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const currentPathRef = useRef<string | null>(null);
  currentPathRef.current = currentPath;
  const [pathDraft, setPathDraft] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [tagFilterTags, setTagFilterTags] = useState<string[]>([]);
  const [tagIncludeSubtree, setTagIncludeSubtree] = useState(true);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagSearchDraft, setTagSearchDraft] = useState('');
  const [tagAcOpen, setTagAcOpen] = useState(false);
  const [tagAcIndex, setTagAcIndex] = useState(0);
  const [subtreeHits, setSubtreeHits] = useState<DsEntry[] | null>(null);
  const [subtreeLoading, setSubtreeLoading] = useState(false);
  const tagSearchInputRef = useRef<HTMLInputElement>(null);
  const [tagEditor, setTagEditor] = useState<{
    paths: string[];
    draft: string;
    /** Tags currently on the selection (union). */
    current: string[];
    /** Tree suggestions for quick add (excluding current). */
    suggestions: string[];
  } | null>(null);
  const [homePath, setHomePath] = useState<string | null>(null);
  const [history, setHistory] = useState<Hist[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [entries, setEntries] = useState<DsEntry[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clipboard, setClipboard] = useState<FileClipboardPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState<string | null>(null);
  const [dropHover, setDropHover] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [iconMenuOpen, setIconMenuOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const [ctx, setCtx] = useState<CtxMenu | null>(null);
  const [propEntries, setPropEntries] = useState<DsEntry[] | null>(null);
  const [du, setDu] = useState<{ bytes: number; files: number; folders: number } | null>(null);
  const [duLoading, setDuLoading] = useState(false);
  const [marquee, setMarquee] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const [thumbByPath, setThumbByPath] = useState<Record<string, string>>({});
  const [vpBox, setVpBox] = useState({ w: 0, h: 0, scrollTop: 0 });
  const [gridMetrics, setGridMetrics] = useState<GridMetrics>(EMPTY_GRID);
  /** 空白鍵預覽：按住空白 + hover 有縮圖時，在原圖示位置放大浮起 3.5×。 */
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [thumbPeek, setThumbPeek] = useState<{
    path: string;
    src: string;
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  /** Hover 資料夾時顯示其 tags。 */
  const [folderTagTip, setFolderTagTip] = useState<{
    path: string;
    tags: string[];
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const spaceHeldRef = useRef(false);
  const hoverThumbPathRef = useRef<string | null>(null);

  // ——— 使用者偏好（檢視／排序／圖示大小） ———
  const [Userpreference, setUserpreference] = useState<Userpreference>(() =>
    loadFileBrowserPreference(desktopControll?.sessionUser)
  );
  const { viewMode, showHiddenFiles, foldersFirst, sortKey, sortAsc, bookmark } = Userpreference;
  const iconSize = clampIconSize(Userpreference.iconSize);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  /** 改偏好並交給桌面 debounce 存檔（本機立刻生效，約 450ms 後上傳）。 */
  const patchUserpreference = (updater: (prev: Userpreference) => Userpreference) => {
    setUserpreference((prev) => {
      const next = updater(prev);
      desktopControll?.saveUserPreference?.(fileBrowserConfig.appID, next);
      return next;
    });
  };

  useEffect(() => {
    setUserpreference(loadFileBrowserPreference(desktopControll?.sessionUser));
  }, [desktopControll?.sessionUser]);

  useEffect(() => {
    setHomePath(effectiveHomePath(desktopControll?.sessionUser));
  }, [desktopControll?.sessionUser]);

  // ——— 啟動：視窗標題、初始資料夾、剪貼簿、點外面關選單 ———

  useEffect(() => {
    if (!props.taskID || !currentPath) {
      return;
    }
    const name = basename(currentPath) || '/';
    desktopControll?.setTaskTitle?.(props.taskID, name);
  }, [props.taskID, currentPath, desktopControll]);

  /** 開窗：用 openPath 參數或使用者家目錄當起點。 */
  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setBootReady(false);
      let home = '/';
      try {
        const res = await fileBrowserApi.whoAmI();
        const user = res.data?.data;
        home = effectiveHomePath(user);
        if (!cancelled && !hasChosenHome(user)) {
          desktopControll?.openFileBrowserSettings?.();
        }
      } catch {
        /* ignore */
      }
      const open = props.taskInitParms?.find((p) => p.command === 'openPath' && p.value);
      const start = open?.value?.trim() || home;
      if (!cancelled) {
        setHomePath(home);
        setHistory([{ uri: start, scrollPX: 0 }]);
        setHistoryIndex(0);
        setCurrentPath(start);
        setPathDraft(start);
        setBootReady(true);
      }
    };
    void boot();
    return () => {
      cancelled = true;
    };
  }, [props.taskInitParms]);

  useEffect(() => fileClipboardService.subscribe(setClipboard), []);

  useEffect(() => {
    const closeCtx = (ev: Event) => {
      if (ctxMenuRef.current?.contains(ev.target as Node)) {
        return;
      }
      setCtx(null);
    };
    const closeProp = (ev: Event) => {
      if (propRef.current?.contains(ev.target as Node)) {
        return;
      }
      if (tagEditorRef.current?.contains(ev.target as Node)) {
        return;
      }
      setPropEntries(null);
    };
    const closeTagEditor = (ev: Event) => {
      if (tagEditorRef.current?.contains(ev.target as Node)) {
        return;
      }
      setTagEditor(null);
    };
    const closePopovers = () => {
      setSortOpen(false);
      setIconMenuOpen(false);
    };
    const closeAll = (ev?: Event) => {
      if (ev) {
        closeCtx(ev);
        closeProp(ev);
        closeTagEditor(ev);
      } else {
        setCtx(null);
        setPropEntries(null);
        setTagEditor(null);
      }
      closePopovers();
    };
    window.addEventListener('pointerdown', closeCtx, true);
    window.addEventListener('pointerdown', closeProp, true);
    window.addEventListener('pointerdown', closeTagEditor, true);
    window.addEventListener('click', closePopovers);
    window.addEventListener('blur', closeAll);
    window.addEventListener('resize', closeAll);
    return () => {
      window.removeEventListener('pointerdown', closeCtx, true);
      window.removeEventListener('pointerdown', closeProp, true);
      window.removeEventListener('pointerdown', closeTagEditor, true);
      window.removeEventListener('click', closePopovers);
      window.removeEventListener('blur', closeAll);
      window.removeEventListener('resize', closeAll);
    };
  }, []);

  useEffect(() => {
    const onDeleted = (ev: Event) => {
      const path = (ev as CustomEvent<{ path?: string }>).detail?.path;
      if (!path) {
        return;
      }
      setEntries((prev) => prev.filter((e) => e.path !== path));
      setSelected((prev) => prev.filter((p) => p !== path));
    };
    window.addEventListener('argon-file-deleted', onDeleted);
    return () => window.removeEventListener('argon-file-deleted', onDeleted);
  }, []);

  // ——— 列出資料夾（hardFresh 會清掉該夾已快取的縮圖） ———

  const clearFolderThumbnailState = useCallback((folderEntries: DsEntry[]) => {
    const imagePaths = folderEntries.filter(isThumbnailCandidate).map((e) => e.path);
    if (imagePaths.length === 0) {
      return;
    }
    imagePaths.forEach((p) => {
      requestedRef.current.delete(p);
      visibleImagePathsRef.current.delete(p);
    });
    setThumbByPath((prev) => {
      const next = { ...prev };
      imagePaths.forEach((p) => {
        if (next[p]) {
          URL.revokeObjectURL(next[p]);
          delete next[p];
        }
      });
      return next;
    });
  }, []);

  /** 向後端列目錄；gen 用來丟掉過期的回應。hardFresh 略過快取並清縮圖。 */
  const refresh = useCallback(async (path: string, opts?: { hardFresh?: boolean }) => {
    const gen = ++refreshGenRef.current;
    if (pendingScrollRef.current == null) {
      pendingScrollRef.current = viewportRef.current?.scrollTop ?? 0;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fileBrowserApi.dsLs(path, { hardFresh: opts?.hardFresh });
      if (gen !== refreshGenRef.current) {
        return;
      }
      const body = res.data;
      if (!body.success) {
        setEntries([]);
        setError(body.message || 'Failed to list directory');
        return;
      }
      const nextEntries = body.data || [];
      if (opts?.hardFresh) {
        clearFolderThumbnailState(nextEntries);
      }
      setEntries(nextEntries);
      setSelected([]);
      setAnchorIndex(null);
      setPathDraft(path);
      setCtx(null);
    } catch (e: any) {
      if (gen !== refreshGenRef.current) {
        return;
      }
      setEntries([]);
      setError(e?.response?.data?.message || e?.message || 'List failed');
    } finally {
      if (gen === refreshGenRef.current) {
        setLoading(false);
        const y = pendingScrollRef.current;
        if (y != null) {
          pendingScrollRef.current = null;
          requestAnimationFrame(() => {
            if (viewportRef.current) {
              viewportRef.current.scrollTop = y;
            }
          });
        }
      }
    }
  }, [clearFolderThumbnailState]);

  useEffect(() => {
    if (!bootReady || !currentPath) {
      return;
    }
    void refresh(currentPath);
  }, [bootReady, currentPath, refresh]);

  useEffect(() => {
    const onChanged = (ev: Event) => {
      const path = (ev as CustomEvent<{ path?: string }>).detail?.path;
      const cur = currentPathRef.current;
      if (!bootReady || !cur || !path) {
        return;
      }
      const norm = (p: string) => p.replace(/\/+$/, '') || '/';
      if (norm(cur) === norm(path)) {
        void refresh(cur);
      }
    };
    window.addEventListener(DS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(DS_CHANGED_EVENT, onChanged);
  }, [bootReady, refresh]);

  // ——— 過濾／排序後的可見項目 + 虛擬捲動切片 ———

  const existingPaths = useMemo(() => new Set(entries.map((e) => e.path)), [entries]);
  const sourceEntries = useMemo(() => {
    if (tagIncludeSubtree && tagFilterTags.length > 0) {
      return subtreeHits ?? [];
    }
    return entries;
  }, [entries, tagIncludeSubtree, tagFilterTags, subtreeHits]);
  const visibleEntries = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    const filtered = sourceEntries.filter((e) => {
      if (!showHiddenFiles && e.name.startsWith('.')) {
        return false;
      }
      if (q && !e.name.toLowerCase().includes(q)) {
        return false;
      }
      if (!tagIncludeSubtree && tagFilterTags.length > 0 && !entryHasAllTags(e, tagFilterTags)) {
        return false;
      }
      return true;
    });
    const dir = sortAsc ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (foldersFirst && a.directory !== b.directory) {
        return a.directory ? -1 : 1;
      }
      const cmp = compareEntries(a, b, sortKey);
      if (cmp !== 0) {
        return cmp * dir;
      }
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [
    sourceEntries,
    showHiddenFiles,
    foldersFirst,
    sortKey,
    sortAsc,
    filterQuery,
    tagFilterTags,
    tagIncludeSubtree,
  ]);

  const tagAcSuggestions = useMemo(() => {
    const q = tagSearchDraft.trim().toLowerCase();
    return availableTags
      .filter((t) => !tagFilterTags.includes(t))
      .filter((t) => !q || t.toLowerCase().includes(q))
      .slice(0, 20);
  }, [availableTags, tagFilterTags, tagSearchDraft]);
  const selectedEntries = useMemo(
    () => visibleEntries.filter((e) => selectedSet.has(e.path)),
    [visibleEntries, selectedSet]
  );

  const iconCols = Math.max(1, gridMetrics.cols);
  const iconRows = visibleEntries.length > 0 ? Math.ceil(visibleEntries.length / iconCols) : 0;
  /** 目前視窗該畫的項目範圍；其餘用空白佔位撐出捲動高度。 */
  const virtualRange = useMemo(() => {
    if (viewMode === 'icons') {
      return visibleSlice(
        vpBox.scrollTop,
        vpBox.h,
        visibleEntries.length,
        iconCols,
        gridMetrics.cellH,
        gridMetrics.gapY,
        gridMetrics.padT
      );
    }
    return visibleSlice(
      vpBox.scrollTop,
      vpBox.h,
      visibleEntries.length,
      1,
      gridMetrics.cellH,
      gridMetrics.gapY,
      gridMetrics.padT + gridMetrics.headerH
    );
  }, [
    viewMode,
    vpBox.scrollTop,
    vpBox.h,
    visibleEntries.length,
    iconCols,
    gridMetrics.cellH,
    gridMetrics.gapY,
    gridMetrics.padT,
    gridMetrics.headerH,
  ]);
  const virtualItems = useMemo(() => {
    const items: { entry: DsEntry; index: number }[] = [];
    for (let i = virtualRange.start; i < virtualRange.end; i += 1) {
      const entry = visibleEntries[i];
      if (entry) {
        items.push({ entry, index: i });
      }
    }
    return items;
  }, [virtualRange, visibleEntries]);
  const iconStartRow = Math.floor(virtualRange.start / iconCols);

  visibleEntriesRef.current = visibleEntries;
  viewModeRef.current = viewMode;
  gridMetricsRef.current = gridMetrics;

  /** 追蹤視窗大小與捲動，更新格子尺寸給虛擬列表／框選用。 */
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    const apply = () => {
      setVpBox((prev) => {
        const next = { w: el.clientWidth, h: el.clientHeight, scrollTop: el.scrollTop };
        if (prev.w === next.w && prev.h === next.h && prev.scrollTop === next.scrollTop) {
          return prev;
        }
        return next;
      });
      const grid = gridRef.current;
      if (!grid) {
        return;
      }
      const next = readGridMetrics(grid, viewModeRef.current);
      setGridMetrics((prev) => {
        if (
          prev.cols === next.cols &&
          prev.cellW === next.cellW &&
          prev.cellH === next.cellH &&
          prev.gapX === next.gapX &&
          prev.gapY === next.gapY &&
          prev.padL === next.padL &&
          prev.padT === next.padT &&
          prev.headerH === next.headerH
        ) {
          return prev;
        }
        return next;
      });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    const grid = gridRef.current;
    if (grid) {
      ro.observe(grid);
    }
    let raf = 0;
    const onScroll = () => {
      if (raf) {
        return;
      }
      raf = requestAnimationFrame(() => {
        raf = 0;
        apply();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', onScroll);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [bootReady, viewMode, iconSize, loading, visibleEntries.length]);

  // ——— 縮圖：只抓畫面上的點陣圖／影片，換資料夾就整批釋放 ———

  useEffect(() => {
    thumbByPathRef.current = thumbByPath;
  }, [thumbByPath]);

  const clearThumbnails = useCallback(() => {
    thumbAbortRef.current?.abort();
    thumbAbortRef.current = null;
    thumbRunRef.current += 1;
    thumbInFlightRef.current = false;
    requestedRef.current.clear();
    visibleImagePathsRef.current.clear();
    if (thumbScheduleRef.current) {
      clearTimeout(thumbScheduleRef.current);
      thumbScheduleRef.current = null;
    }
    setThumbByPath((prev) => {
      Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
      return {};
    });
  }, []);

  useEffect(() => {
    clearThumbnails();
  }, [currentPath, clearThumbnails]);

  useEffect(() => () => {
    thumbAbortRef.current?.abort();
    Object.values(thumbByPathRef.current).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const collectVisibleImagePaths = useCallback((): string[] => {
    const mounted = new Set(virtualItems.map((v) => v.entry.path));
    const observed = Array.from(visibleImagePathsRef.current).filter((p) => mounted.has(p));
    if (observed.length > 0) {
      return observed;
    }
    return virtualItems.filter((v) => isThumbnailCandidate(v.entry)).map((v) => v.entry.path);
  }, [virtualItems]);

  const releasePending = (paths: string[]) => {
    paths.forEach((p) => requestedRef.current.delete(p));
  };

  /** 分批向後端要可見檔案的縮圖，轉成 blob URL 給 <img> 用。 */
  const requestVisibleThumbnails = useCallback(async () => {
    if (thumbInFlightRef.current) {
      queueThumbnailFetchRef.current?.();
      return;
    }

    const visible = collectVisibleImagePaths();
    const pending = visible.filter(
      (p) => !requestedRef.current.has(p) && !thumbByPathRef.current[p]
    );
    if (pending.length === 0) {
      return;
    }

    thumbInFlightRef.current = true;
    const runId = thumbRunRef.current;
    const ac = new AbortController();
    thumbAbortRef.current = ac;

    try {
      for (const batch of chunk(pending, THUMB_BATCH)) {
        if (ac.signal.aborted || runId !== thumbRunRef.current) {
          releasePending(pending);
          return;
        }

        const batchPending = batch.filter(
          (p) => !requestedRef.current.has(p) && !thumbByPathRef.current[p]
        );
        if (batchPending.length === 0) {
          continue;
        }
        batchPending.forEach((p) => requestedRef.current.add(p));

        const res = await fileBrowserApi.fetchThumbnails(batchPending, ac.signal);
        if (ac.signal.aborted || runId !== thumbRunRef.current) {
          releasePending(batchPending);
          return;
        }

        const updates: Record<string, string> = {};
        for (const item of res.items || []) {
          if (item.skipped || !item.dataBase64 || !item.contentType) {
            continue;
          }
          const binary = atob(item.dataBase64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: item.contentType });
          updates[item.path] = URL.createObjectURL(blob);
        }
        if (Object.keys(updates).length > 0) {
          setThumbByPath((prev) => ({ ...prev, ...updates }));
        }
      }
    } catch (e: any) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
        releasePending(pending);
        return;
      }
      releasePending(pending);
    } finally {
      thumbInFlightRef.current = false;
      if (thumbAbortRef.current === ac) {
        thumbAbortRef.current = null;
      }
    }
  }, [collectVisibleImagePaths]);

  /** 捲動時 debounce，避免每一幀都打縮圖 API。 */
  const queueThumbnailFetch = useCallback((delayMs = 150) => {
    if (thumbScheduleRef.current) {
      clearTimeout(thumbScheduleRef.current);
    }
    thumbScheduleRef.current = setTimeout(() => {
      thumbScheduleRef.current = null;
      void requestVisibleThumbnails();
    }, delayMs);
  }, [requestVisibleThumbnails]);

  queueThumbnailFetchRef.current = queueThumbnailFetch;

  /** 用 IntersectionObserver 記錄哪些縮圖候選在畫面上，再排隊抓縮圖。 */
  useLayoutEffect(() => {
    if (loading) {
      visibleImagePathsRef.current.clear();
      return;
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;

    const setup = () => {
      if (cancelled) {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          let changed = false;
          for (const ent of entries) {
            const path = (ent.target as HTMLElement).dataset.fbPath;
            if (!path) {
              continue;
            }
            if (ent.isIntersecting) {
              if (!visibleImagePathsRef.current.has(path)) {
                visibleImagePathsRef.current.add(path);
                changed = true;
              }
            } else if (visibleImagePathsRef.current.delete(path)) {
              changed = true;
            }
          }
          if (changed) {
            queueThumbnailFetch();
          }
        },
        { root: null, rootMargin: '80px', threshold: 0.05 }
      );

      itemRefs.current.forEach((el, path) => {
        const entry = visibleEntries.find((e) => e.path === path);
        if (entry && isThumbnailCandidate(entry)) {
          observer?.observe(el);
        }
      });

      queueThumbnailFetch(0);
    };

    const raf = requestAnimationFrame(setup);

    const onScroll = () => queueThumbnailFetch();
    window.addEventListener('scroll', onScroll, true);
    const vpEl = viewportRef.current;
    vpEl?.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('scroll', onScroll, true);
      vpEl?.removeEventListener('scroll', onScroll);
      if (thumbScheduleRef.current) {
        clearTimeout(thumbScheduleRef.current);
        thumbScheduleRef.current = null;
      }
    };
  }, [loading, virtualItems, viewMode, queueThumbnailFetch]);

  useEffect(() => {
    if (loading) {
      return;
    }
    const timer = window.setTimeout(() => {
      void requestVisibleThumbnails();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loading, currentPath, virtualItems, viewMode, requestVisibleThumbnails]);

  const entryIconSrc = (entry: DsEntry) =>
    thumbByPath[entry.path] ?? resolveEntryIcon(entry.name, entry.directory);

  const entryIconClass = (entry: DsEntry) =>
    `fb-ico${thumbByPath[entry.path] ? ' fb-ico-thumb' : ''}`;

  /** 量測縮圖位置；浮起 3.5×，保留真實長寬比（唔強制 1:1）。 */
  const showThumbPeekFor = useCallback(
    (entry: DsEntry, itemEl: HTMLElement | null) => {
      const src = thumbByPathRef.current[entry.path];
      if (!src || !spaceHeldRef.current || !itemEl || !rootRef.current) {
        setThumbPeek(null);
        return;
      }
      const img = itemEl.querySelector('.fb-ico') as HTMLImageElement | null;
      if (!img) {
        setThumbPeek(null);
        return;
      }
      const rootBox = rootRef.current.getBoundingClientRect();
      const imgBox = img.getBoundingClientRect();
      const nw = img.naturalWidth > 0 ? img.naturalWidth : imgBox.width;
      const nh = img.naturalHeight > 0 ? img.naturalHeight : imgBox.height;
      const base = Math.max(imgBox.width, imgBox.height) * 3.5;
      const ratio = nw / Math.max(nh, 1);
      const width = ratio >= 1 ? base : base * ratio;
      const height = ratio >= 1 ? base / ratio : base;
      const cx = imgBox.left + imgBox.width / 2 - rootBox.left;
      const cy = imgBox.top + imgBox.height / 2 - rootBox.top;
      hoverThumbPathRef.current = entry.path;
      setThumbPeek({
        path: entry.path,
        src,
        left: cx - width / 2,
        top: cy - height / 2,
        width,
        height,
      });
    },
    []
  );

  const onItemPointerEnter = useCallback(
    (entry: DsEntry, ev: React.MouseEvent<HTMLElement>) => {
      if (entry.directory) {
        const tags = entryTags(entry);
        if (tags.length > 0 && rootRef.current) {
          const rootBox = rootRef.current.getBoundingClientRect();
          const box = ev.currentTarget.getBoundingClientRect();
          setFolderTagTip({
            path: entry.path,
            tags,
            left: box.left - rootBox.left,
            top: box.bottom - rootBox.top + 4,
            width: box.width,
          });
        } else {
          setFolderTagTip(null);
        }
      } else {
        setFolderTagTip(null);
      }
      if (!thumbByPathRef.current[entry.path]) {
        hoverThumbPathRef.current = null;
        return;
      }
      hoverThumbPathRef.current = entry.path;
      if (spaceHeldRef.current) {
        showThumbPeekFor(entry, ev.currentTarget);
      }
    },
    [showThumbPeekFor]
  );

  const onItemPointerLeave = useCallback((entry: DsEntry) => {
    setFolderTagTip((prev) => (prev?.path === entry.path ? null : prev));
    if (hoverThumbPathRef.current === entry.path) {
      hoverThumbPathRef.current = null;
      setThumbPeek(null);
    }
  }, []);

  useEffect(() => {
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) {
        return false;
      }
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.code !== 'Space' && ev.key !== ' ') {
        return;
      }
      if (isTypingTarget(ev.target)) {
        return;
      }
      if (!rootRef.current?.matches(':hover') && !rootRef.current?.contains(document.activeElement)) {
        return;
      }
      // 阻止瀏覽器用 Space 向下捲動（含 key repeat）
      ev.preventDefault();
      if (ev.repeat) {
        return;
      }
      spaceHeldRef.current = true;
      setSpaceHeld(true);
      const path = hoverThumbPathRef.current;
      if (path) {
        const entry = visibleEntriesRef.current.find((e) => e.path === path);
        const el = itemRefs.current.get(path) ?? null;
        if (entry) {
          showThumbPeekFor(entry, el);
        }
      }
    };

    const onKeyUp = (ev: KeyboardEvent) => {
      if (ev.code !== 'Space' && ev.key !== ' ') {
        return;
      }
      spaceHeldRef.current = false;
      setSpaceHeld(false);
      setThumbPeek(null);
    };

    const onBlur = () => {
      spaceHeldRef.current = false;
      setSpaceHeld(false);
      setThumbPeek(null);
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onBlur);
    };
  }, [showThumbPeekFor]);

  // 捲動時關掉浮起預覽（位置會錯）
  useEffect(() => {
    if (!thumbPeek && !folderTagTip) {
      return;
    }
    const vp = viewportRef.current;
    if (!vp) {
      return;
    }
    const onScroll = () => {
      setThumbPeek(null);
      setFolderTagTip(null);
    };
    vp.addEventListener('scroll', onScroll, { passive: true });
    return () => vp.removeEventListener('scroll', onScroll);
  }, [thumbPeek, folderTagTip]);

  // ——— 導覽：歷史紀錄、上一層、開啟資料夾／圖片 ———

  const clearNameFilter = useCallback(() => {
    setFilterOpen(false);
    setFilterQuery('');
  }, []);

  const clearTagFilter = useCallback(() => {
    setTagFilterOpen(false);
    setTagFilterTags([]);
    setTagIncludeSubtree(true);
    setTagSearchDraft('');
    setTagAcOpen(false);
    setSubtreeHits(null);
  }, []);

  const clearFilter = useCallback(() => {
    clearNameFilter();
    clearTagFilter();
  }, [clearNameFilter, clearTagFilter]);

  const toggleFilter = useCallback(() => {
    if (filterOpen) {
      clearNameFilter();
    } else {
      setFilterOpen(true);
    }
  }, [filterOpen, clearNameFilter]);

  const toggleTagFilter = useCallback(() => {
    if (tagFilterOpen) {
      clearTagFilter();
    } else {
      setTagFilterOpen(true);
    }
  }, [tagFilterOpen, clearTagFilter]);

  const onFilterQueryChange = useCallback((value: string) => {
    setFilterQuery(value);
    setSelected([]);
    setAnchorIndex(null);
  }, []);

  useEffect(() => {
    if (filterOpen) {
      filterInputRef.current?.focus();
    }
  }, [filterOpen]);

  useEffect(() => {
    if (tagFilterOpen) {
      tagSearchInputRef.current?.focus();
    }
  }, [tagFilterOpen]);

  /** Tag 搜尋開啟或路徑變更時，載入這棵樹的 tags 供 autocomplete。 */
  useEffect(() => {
    if (!tagFilterOpen || !currentPath) {
      return;
    }
    let cancelled = false;
    void fileBrowserApi
      .tagsInPath(currentPath)
      .then((res) => {
        if (!cancelled && res.data?.success) {
          setAvailableTags(res.data.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableTags([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tagFilterOpen, currentPath]);

  /** Include subfolders（預設開）：用 find API 拉扁平結果。 */
  useEffect(() => {
    if (!tagIncludeSubtree || tagFilterTags.length === 0 || !currentPath) {
      setSubtreeHits(null);
      setSubtreeLoading(false);
      return;
    }
    let cancelled = false;
    setSubtreeLoading(true);
    void fileBrowserApi
      .tagsFindPaths(currentPath, tagFilterTags)
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (res.data?.success) {
          setSubtreeHits(res.data.data || []);
        } else {
          setSubtreeHits([]);
          setError(res.data?.message || 'Tag search failed');
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          setSubtreeHits([]);
          setError(e?.response?.data?.message || e?.message || 'Tag search failed');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSubtreeLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tagIncludeSubtree, tagFilterTags, currentPath]);

  const addTagFilter = useCallback((tag: string) => {
    const t = tag.trim();
    if (!t) {
      return;
    }
    setTagFilterTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setTagSearchDraft('');
    setTagAcOpen(false);
    setSelected([]);
    setAnchorIndex(null);
  }, []);

  const removeTagFilter = useCallback((tag: string) => {
    setTagFilterTags((prev) => prev.filter((t) => t !== tag));
    setSelected([]);
    setAnchorIndex(null);
  }, []);

  const openTagEditor = useCallback(
    async (paths: string[]) => {
      if (paths.length === 0) {
        return;
      }
      let suggestions: string[] = [];
      if (currentPath) {
        try {
          const res = await fileBrowserApi.tagsInPath(currentPath);
          if (res.data?.success) {
            suggestions = res.data.data || [];
          }
        } catch {
          /* ignore */
        }
      }
      const byPath = new Map<string, DsEntry>();
      entries.forEach((e) => byPath.set(e.path, e));
      (subtreeHits || []).forEach((e) => byPath.set(e.path, e));
      selectedEntries.forEach((e) => byPath.set(e.path, e));
      const union = new Set<string>();
      for (const p of paths) {
        const e = byPath.get(p);
        if (e) {
          entryTags(e).forEach((t) => union.add(t));
        }
      }
      const current = Array.from(union).sort((a, b) => a.localeCompare(b));
      setTagEditor({
        paths,
        draft: '',
        current,
        suggestions: suggestions.filter((t) => !union.has(t)),
      });
      setCtx(null);
    },
    [currentPath, entries, selectedEntries, subtreeHits]
  );

  const navigateTo = useCallback((path: string, opts?: { push?: boolean; scrollPX?: number }) => {
    const uri = path.trim() || '/';
    const y = viewportRef.current?.scrollTop ?? 0;
    pendingScrollRef.current = opts?.push === false ? (opts.scrollPX ?? 0) : 0;
    setHistory((prev) => {
      const cur = historyIndex >= 0 ? historyIndex : -1;
      const stamped =
        cur >= 0 && prev[cur] ? prev.map((h, i) => (i === cur ? { ...h, scrollPX: y } : h)) : prev;
      if (opts?.push === false) {
        return stamped;
      }
      const base = cur >= 0 ? stamped.slice(0, cur + 1) : [];
      if (base.length > 0 && base[base.length - 1].uri === uri) {
        return base;
      }
      setHistoryIndex(base.length);
      return [...base, { uri, scrollPX: 0 }];
    });
    if (currentPathRef.current !== uri) {
      clearFilter();
    }
    setCurrentPath(uri);
  }, [historyIndex, clearFilter]);

  const goHistory = (dir: -1 | 1) => {
    const item = history[historyIndex + dir];
    if (!item) {
      return;
    }
    setHistoryIndex(historyIndex + dir);
    navigateTo(item.uri, { push: false, scrollPX: item.scrollPX });
  };

  const goUp = () => {
    if (!currentPath) {
      return;
    }
    const parent = parentPath(currentPath);
    if (parent) {
      navigateTo(parent);
    }
  };

  const goHome = () => {
    navigateTo(homePath || '/');
  };

  const addBookmark = (path: string) => {
    const n = normalizeBookmarkPath(path);
    if (!n) {
      return;
    }
    patchUserpreference((prev) => {
      if (prev.bookmark.includes(n)) {
        return prev;
      }
      return { ...prev, bookmark: [...prev.bookmark, n] };
    });
    setStatusNote('Bookmarked');
  };

  const removeBookmark = (path: string) => {
    const n = normalizeBookmarkPath(path);
    patchUserpreference((prev) => ({
      ...prev,
      bookmark: prev.bookmark.filter((p) => p !== n),
    }));
  };

  const openDir = (entry: DsEntry) => {
    if (entry.directory) {
      navigateTo(entry.path);
    }
  };

  const openImage = useCallback(
    (entry: DsEntry) => {
      const images = visibleEntries
        .filter((e) => !e.directory && isImageFile(e.name))
        .map((e) => e.path);
      desktopControll?.openingTask?.(
        'imageViewer',
        [
          { command: 'imagePath', value: entry.path },
          { command: 'imageList', value: JSON.stringify(images) },
        ]
      );
    },
    [desktopControll, visibleEntries]
  );

  const openVideo = useCallback(
    (entry: DsEntry) => {
      const videos = visibleEntries
        .filter((e) => !e.directory && isNativeVideoFile(e.name))
        .map((e) => e.path);
      desktopControll?.openingTask?.(
        'videoPlayer',
        [
          { command: 'videoPath', value: entry.path },
          { command: 'videoList', value: JSON.stringify(videos) },
        ],
        entry.name
      );
    },
    [desktopControll, visibleEntries]
  );

  const openPdf = useCallback(
    (entry: DsEntry) => {
      desktopControll?.openingTask?.(
        'pdfViewer',
        [{ command: 'pdfPath', value: entry.path }],
        entry.name
      );
    },
    [desktopControll]
  );

  const sendToAudioPlayer = useCallback(
    (parms: { command: string; value: string }[], title?: string) => {
      if (desktopControll?.openOrReuseTask) {
        desktopControll.openOrReuseTask('audioPlayer', parms, title);
        return;
      }
      desktopControll?.openingTask?.('audioPlayer', parms, title);
    },
    [desktopControll]
  );

  const openAudio = useCallback(
    (entry: DsEntry) => {
      sendToAudioPlayer(
        [
          { command: 'op', value: 'replace' },
          { command: 'audioPath', value: entry.path },
          { command: 'audioList', value: JSON.stringify([entry.path]) },
          { command: 'nonce', value: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
        ],
        entry.name
      );
    },
    [sendToAudioPlayer]
  );

  const addToPlaylist = useCallback(
    (targets: DsEntry[]) => {
      if (targets.length === 0) {
        return;
      }
      const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const singleMusic =
        targets.length === 1 && !targets[0].directory && isNativeAudioFile(targets[0].name);
      if (singleMusic) {
        sendToAudioPlayer([
          { command: 'op', value: 'append' },
          { command: 'audioList', value: JSON.stringify([targets[0].path]) },
          { command: 'nonce', value: nonce },
        ]);
        return;
      }
      sendToAudioPlayer([
        { command: 'op', value: 'scan' },
        { command: 'scanPaths', value: JSON.stringify(targets.map((e) => e.path)) },
        { command: 'nonce', value: nonce },
      ]);
    },
    [sendToAudioPlayer]
  );

  const openUnsupported = useCallback(
    (entry: DsEntry) => {
      desktopControll?.openingTask?.(
        'unsupportedFile',
        [
          { command: 'filePath', value: entry.path },
          { command: 'fileName', value: entry.name },
        ],
        entry.name
      );
    },
    [desktopControll]
  );

  const openEntry = (entry: DsEntry) => {
    if (entry.directory) {
      openDir(entry);
    } else if (isImageFile(entry.name)) {
      openImage(entry);
    } else if (isNativeVideoFile(entry.name)) {
      openVideo(entry);
    } else if (isNativeAudioFile(entry.name)) {
      openAudio(entry);
    } else if (isPdfFile(entry.name)) {
      openPdf(entry);
    } else {
      openUnsupported(entry);
    }
  };

  // ——— 選取：點選、框選（起點綁內容座標，捲動時仍從起點選到終點） ———

  const selectClick = (entry: DsEntry, index: number, ev: React.MouseEvent) => {
    if (ev.shiftKey && anchorIndex != null) {
      const from = Math.min(anchorIndex, index);
      const to = Math.max(anchorIndex, index);
      setSelected(visibleEntries.slice(from, to + 1).map((e) => e.path));
      return;
    }
    if (ev.ctrlKey || ev.metaKey) {
      setSelected((prev) =>
        prev.includes(entry.path) ? prev.filter((p) => p !== entry.path) : [...prev, entry.path]
      );
      setAnchorIndex(index);
      return;
    }
    setSelected([entry.path]);
    setAnchorIndex(index);
  };

  /** 用格子幾何算框選打到哪些項目（不依賴 DOM 節點，虛擬化外的項目也能選到）。 */
  const applyMarqueeSelection = (
    box: { left: number; top: number; right: number; bottom: number },
    additive: boolean,
    origin: string[]
  ) => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    const items = visibleEntriesRef.current;
    const grid = gridRef.current;
    const m = grid ? readGridMetrics(grid, viewModeRef.current) : gridMetricsRef.current;
    gridMetricsRef.current = m;
    if (m.cellH <= 0) {
      return;
    }
    const vp = el.getBoundingClientRect();
    const originX = vp.left - el.scrollLeft;
    const originY = vp.top - el.scrollTop;
    const hit: string[] = [];

    if (viewModeRef.current === 'icons') {
      const cols = Math.max(1, m.cols);
      const rows = Math.ceil(items.length / cols);
      const colRange = overlappingTracks(box.left, box.right, originX + m.padL, m.cellW, m.gapX, cols);
      const rowRange = overlappingTracks(box.top, box.bottom, originY + m.padT, m.cellH, m.gapY, rows);
      if (colRange && rowRange) {
        for (let row = rowRange.start; row <= rowRange.end; row += 1) {
          for (let col = colRange.start; col <= colRange.end; col += 1) {
            const i = row * cols + col;
            if (i < items.length) {
              hit.push(items[i].path);
            }
          }
        }
      }
    } else {
      const rowRange = overlappingTracks(
        box.top,
        box.bottom,
        originY + m.padT + m.headerH,
        m.cellH,
        m.gapY,
        items.length
      );
      if (rowRange) {
        for (let i = rowRange.start; i <= rowRange.end; i += 1) {
          hit.push(items[i].path);
        }
      }
    }

    const next = additive ? Array.from(new Set([...origin, ...hit])) : hit;
    setSelected((prev) => (samePathSet(prev, next) ? prev : next));
  };

  /** 空白處拖曳開始框選；靠近邊緣會自動捲動。 */
  const onViewportMouseDown = (ev: React.MouseEvent) => {
    if (ev.button !== 0) {
      return;
    }
    const vpEl = viewportRef.current;
    if (vpEl) {
      const r = vpEl.getBoundingClientRect();
      if (ev.clientX > r.left + vpEl.clientWidth || ev.clientY > r.top + vpEl.clientHeight) {
        return;
      }
    }
    const target = ev.target as HTMLElement;
    vpEl?.focus();
    if (target.closest('[data-fb-item="1"]')) {
      return;
    }
    setCtx(null);
    const additive = ev.ctrlKey || ev.metaKey || ev.shiftKey;
    dragRef.current = {
      active: true,
      additive,
      startX: ev.clientX,
      startY: ev.clientY,
      startScrollLeft: vpEl?.scrollLeft ?? 0,
      startScrollTop: vpEl?.scrollTop ?? 0,
      lastX: ev.clientX,
      lastY: ev.clientY,
      originSelected: additive ? [...selected] : [],
    };
    if (!additive) {
      setSelected([]);
      setAnchorIndex(null);
    }
    const origin = rootRef.current?.getBoundingClientRect();
    setMarquee({
      x: ev.clientX - (origin?.left ?? 0),
      y: ev.clientY - (origin?.top ?? 0),
      w: 0,
      h: 0,
    });

    let moveRaf = 0;
    let autoRaf = 0;
    const flushMove = () => {
      moveRaf = 0;
      const d = dragRef.current;
      const el = viewportRef.current;
      if (!d?.active || !el) {
        return;
      }
      // 起點用「按下時的內容位置」換算回目前螢幕座標，捲動時框才會跟著拉長
      const startX = d.startX - (el.scrollLeft - d.startScrollLeft);
      const startY = d.startY - (el.scrollTop - d.startScrollTop);
      const left = Math.min(startX, d.lastX);
      const top = Math.min(startY, d.lastY);
      const right = Math.max(startX, d.lastX);
      const bottom = Math.max(startY, d.lastY);
      const origin = rootRef.current?.getBoundingClientRect();
      const vp = el.getBoundingClientRect();
      const visLeft = Math.max(left, vp.left);
      const visTop = Math.max(top, vp.top);
      const visRight = Math.min(right, vp.right);
      const visBottom = Math.min(bottom, vp.bottom);
      if (origin && visRight > visLeft && visBottom > visTop) {
        setMarquee({
          x: visLeft - origin.left,
          y: visTop - origin.top,
          w: visRight - visLeft,
          h: visBottom - visTop,
        });
      }
      applyMarqueeSelection({ left, top, right, bottom }, d.additive, d.originSelected);
    };
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (d) {
        d.lastX = e.clientX;
        d.lastY = e.clientY;
      }
      if (!moveRaf) {
        moveRaf = requestAnimationFrame(flushMove);
      }
    };
    const onScroll = () => {
      if (!moveRaf) {
        moveRaf = requestAnimationFrame(flushMove);
      }
    };
    const EDGE = 48;
    const tickAutoScroll = () => {
      autoRaf = 0;
      const d = dragRef.current;
      const el = viewportRef.current;
      if (!d?.active || !el) {
        return;
      }
      const r = el.getBoundingClientRect();
      let dy = 0;
      if (d.lastY < r.top + EDGE) {
        const t = Math.min(1, (r.top + EDGE - d.lastY) / EDGE);
        dy = -Math.ceil(4 + 20 * t);
      } else if (d.lastY > r.bottom - EDGE) {
        const t = Math.min(1, (d.lastY - (r.bottom - EDGE)) / EDGE);
        dy = Math.ceil(4 + 20 * t);
      }
      if (dy) {
        const prev = el.scrollTop;
        el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + dy));
        if (el.scrollTop !== prev) {
          flushMove();
        }
      }
      autoRaf = requestAnimationFrame(tickAutoScroll);
    };

    const onUp = () => {
      if (moveRaf) {
        cancelAnimationFrame(moveRaf);
        flushMove();
      }
      if (autoRaf) {
        cancelAnimationFrame(autoRaf);
      }
      dragRef.current = null;
      setMarquee(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      vpEl?.removeEventListener('scroll', onScroll);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    vpEl?.addEventListener('scroll', onScroll, { passive: true });
    autoRaf = requestAnimationFrame(tickAutoScroll);
  };

  // ——— 剪貼簿／新增資料夾／刪除／下載／zip ———

  const withBusy = async (msg: string, fn: () => Promise<void>) => {
    setBusy(true);
    setBusyMsg(msg);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || msg + ' failed');
    } finally {
      setBusy(false);
      setBusyMsg(null);
    }
  };

  const refreshAfterTagChange = async (
    paths: string[],
    mutate: (prev: string[]) => string[]
  ) => {
    const pathSet = new Set(paths);
    const patch = (list: DsEntry[]) =>
      list.map((e) => (pathSet.has(e.path) ? { ...e, tags: mutate(entryTags(e)) } : e));
    setEntries((prev) => patch(prev));
    setPropEntries((prev) => (prev ? patch(prev) : prev));
    setSubtreeHits((prev) => (prev ? patch(prev) : prev));
    const cur = currentPathRef.current;
    if (cur) {
      await refresh(cur);
    }
  };

  const refreshAvailableTags = async () => {
    if (!tagFilterOpen || !currentPath) {
      return;
    }
    try {
      const tip = await fileBrowserApi.tagsInPath(currentPath);
      if (tip.data?.success) {
        setAvailableTags(tip.data.data || []);
      }
    } catch {
      /* ignore */
    }
  };

  const applyAddTags = async (paths: string[], tags: string[], opts?: { keepEditor?: boolean }) => {
    const cleaned = Array.from(new Set(tags.map((t) => t.trim()).filter(Boolean)));
    if (paths.length === 0 || cleaned.length === 0) {
      return;
    }
    await withBusy('Tagging…', async () => {
      const res = await fileBrowserApi.tagsAdd(paths, cleaned);
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Add tags failed');
      }
      setStatusNote(
        cleaned.length === 1 ? `Tagged with ${cleaned[0]}` : `Tagged with ${cleaned.length} tags`
      );
      await refreshAfterTagChange(paths, (prev) =>
        Array.from(new Set([...prev, ...cleaned])).sort((a, b) => a.localeCompare(b))
      );
      if (opts?.keepEditor) {
        setTagEditor((prev) => {
          if (!prev || prev.paths.length !== paths.length || prev.paths.some((p, i) => p !== paths[i])) {
            return prev;
          }
          const current = Array.from(new Set([...prev.current, ...cleaned])).sort((a, b) =>
            a.localeCompare(b)
          );
          return {
            ...prev,
            draft: '',
            current,
            suggestions: prev.suggestions.filter((t) => !current.includes(t)),
          };
        });
      } else {
        setTagEditor(null);
      }
      await refreshAvailableTags();
    });
  };

  const applyRemoveTags = async (paths: string[], tags: string[], opts?: { keepEditor?: boolean }) => {
    const cleaned = Array.from(new Set(tags.map((t) => t.trim()).filter(Boolean)));
    if (paths.length === 0 || cleaned.length === 0) {
      return;
    }
    await withBusy('Removing tags…', async () => {
      const res = await fileBrowserApi.tagsRemove(paths, cleaned);
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Remove tags failed');
      }
      setStatusNote('Tags removed');
      const drop = new Set(cleaned);
      await refreshAfterTagChange(paths, (prev) => prev.filter((t) => !drop.has(t)));
      if (opts?.keepEditor) {
        setTagEditor((prev) => {
          if (!prev || prev.paths.length !== paths.length || prev.paths.some((p, i) => p !== paths[i])) {
            return prev;
          }
          const current = prev.current.filter((t) => !drop.has(t));
          const suggestions = Array.from(new Set([...prev.suggestions, ...cleaned]))
            .filter((t) => !current.includes(t))
            .sort((a, b) => a.localeCompare(b));
          return { ...prev, current, suggestions };
        });
      } else {
        setTagEditor(null);
      }
      await refreshAvailableTags();
    });
  };

  const onRootDragEnter = (e: React.DragEvent) => {
    if (!isFileDrag(e)) {
      return;
    }
    e.preventDefault();
    dropDepthRef.current += 1;
    setDropHover(true);
  };

  const onRootDragOver = (e: React.DragEvent) => {
    if (!isFileDrag(e)) {
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onRootDragLeave = () => {
    dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
    if (dropDepthRef.current === 0) {
      setDropHover(false);
    }
  };

  const onRootDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dropDepthRef.current = 0;
    setDropHover(false);
    if (!isFileDrag(e) || busy || !currentPath) {
      return;
    }
    const snap = snapshotDataTransfer(e.dataTransfer);
    if (snap.entries.length === 0 && snap.files.length === 0) {
      return;
    }
    const session = stashUploadDrop({
      destPath: currentPath,
      existingPaths: Array.from(existingPaths),
      entries: snap.entries,
      files: snap.files,
    });
    const opened = desktopControll?.openingTask?.(
      'fileUploader',
      [{ command: 'session', value: session }],
      currentPath
    );
    if (opened === false) {
      takeUploadDrop(session);
    }
  };

  const doCopy = (paths = selected) => {
    if (paths.length === 0) {
      return;
    }
    fileClipboardService.set('copy', paths);
    setStatusNote(`Copied ${paths.length} item(s)`);
  };

  const doCut = (paths = selected) => {
    if (paths.length === 0) {
      return;
    }
    fileClipboardService.set('cut', paths);
    setStatusNote(`Cut ${paths.length} item(s)`);
  };

  const doPaste = async () => {
    const clip = fileClipboardService.get();
    if (!clip || clip.paths.length === 0 || !currentPath) {
      return;
    }
    const msg = clip.mode === 'cut' ? 'Moving…' : 'Copying…';
    await withBusy(msg, async () => {
      const taken = new Set(existingPaths);
      for (const src of clip.paths) {
        const name = basename(src);
        const dest = uniqueDestPath(currentPath, name, taken);
        taken.add(dest);
        const res =
          clip.mode === 'cut'
            ? await fileBrowserApi.dsMove(src, dest)
            : await fileBrowserApi.dsCopy(src, dest);
        if (!res.data.success) {
          throw new Error(res.data.message || `${clip.mode} failed for ${src}`);
        }
      }
      if (clip.mode === 'cut') {
        fileClipboardService.clear();
      }
      await refresh(currentPath);
    });
  };

  const doMkdir = async () => {
    const name = window.prompt('New folder name');
    if (!name || !name.trim() || !currentPath) {
      return;
    }
    await withBusy('Creating folder…', async () => {
      const path = joinPath(currentPath, name.trim());
      const res = await fileBrowserApi.dsMkdir(path);
      if (!res.data.success) {
        throw new Error(res.data.message || 'mkdir failed');
      }
      await refresh(currentPath);
    });
  };

  const doDelete = async (paths = selected) => {
    if (paths.length === 0 || !currentPath) {
      return;
    }
    const ok = window.confirm(`Delete ${paths.length} item(s)?`);
    if (!ok) {
      return;
    }
    await withBusy('Deleting…', async () => {
      for (const path of paths) {
        const res = await fileBrowserApi.dsDelete(path, true);
        if (!res.data.success) {
          throw new Error(res.data.message || `Delete failed for ${path}`);
        }
      }
      await refresh(currentPath);
    });
  };

  const doRename = async (path: string) => {
    const entry = entries.find((e) => e.path === path);
    const name = window.prompt('Rename', entry?.name || basename(path));
    if (!name || !name.trim() || !currentPath) {
      return;
    }
    const dest = joinPath(currentPath, name.trim());
    if (dest === path) {
      return;
    }
    await withBusy('Renaming…', async () => {
      const res = await fileBrowserApi.dsMove(path, dest);
      if (!res.data.success) {
        throw new Error(res.data.message || 'Rename failed');
      }
      await refresh(currentPath);
    });
  };

  const doDownload = (paths: string[]) => {
    if (paths.length === 0) {
      return;
    }
    if (paths.length === 1) {
      const entry = entries.find((e) => e.path === paths[0]);
      if (entry && !entry.directory) {
        desktopControll?.launchFileDownload?.(entry.path, entry.name);
        return;
      }
      desktopControll?.launchZipDownload?.(paths, `${entry?.name || basename(paths[0])}.zip`);
      return;
    }
    desktopControll?.launchZipDownload?.(paths, 'Archive.zip');
  };

  const doDownloadEach = (paths: string[]) => {
    paths.forEach((p) => {
      const entry = entries.find((e) => e.path === p);
      if (entry && !entry.directory) {
        desktopControll?.launchFileDownload?.(entry.path, entry.name);
      }
    });
  };

  const doZip = async (paths: string[]) => {
    if (paths.length === 0 || !currentPath) {
      return;
    }
    await withBusy('Zipping…', async () => {
      const res = await fileBrowserApi.dsZip(paths, currentPath);
      if (!res.data.success) {
        throw new Error(res.data.message || 'Zip failed');
      }
      await refresh(currentPath);
    });
  };

  const canSetWallpaper = (entry: DsEntry | null | undefined) =>
    !!entry && !entry.directory && isRasterImageFile(entry.name);

  const doSetWallpaper = async (path: string) => {
    if (!window.confirm('Set this image as wallpaper?')) {
      return;
    }
    await withBusy('Setting wallpaper…', async () => {
      const res = await fileBrowserApi.setWallpaper(path);
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed');
      }
      window.dispatchEvent(new Event('argon-wallpaper-changed'));
      setStatusNote('Wallpaper set');
    });
  };

  // ——— 鍵盤捷徑、右鍵選單 ———

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === ' ' || ev.code === 'Space') {
      ev.preventDefault();
      return;
    }
    const mod = ev.ctrlKey || ev.metaKey;
    if (mod && ev.key.toLowerCase() === 'a') {
      ev.preventDefault();
      setSelected(visibleEntries.map((e) => e.path));
      return;
    }
    if (mod && ev.key.toLowerCase() === 'c') {
      ev.preventDefault();
      doCopy();
      return;
    }
    if (mod && ev.key.toLowerCase() === 'x') {
      ev.preventDefault();
      doCut();
      return;
    }
    if (mod && ev.key.toLowerCase() === 'v') {
      ev.preventDefault();
      void doPaste();
      return;
    }
    if (ev.key === 'Delete' || (ev.key === 'Backspace' && mod)) {
      ev.preventDefault();
      void doDelete();
      return;
    }
    if (ev.key === 'Enter' && selectedEntries.length === 1) {
      ev.preventDefault();
      openEntry(selectedEntries[0]);
      return;
    }
    if (ev.altKey && ev.key === 'ArrowLeft') {
      ev.preventDefault();
      goHistory(-1);
      return;
    }
    if (ev.altKey && ev.key === 'ArrowRight') {
      ev.preventDefault();
      goHistory(1);
      return;
    }
    if (ev.key === 'Backspace' && !mod) {
      ev.preventDefault();
      goUp();
    }
  };

  const openContextMenu = (ev: React.MouseEvent, paths: string[]) => {
    ev.preventDefault();
    ev.stopPropagation();
    let menuPaths = paths;
    if (paths.length > 0) {
      const keep = paths.some((p) => selected.includes(p));
      if (keep) {
        menuPaths = selected;
      } else {
        setSelected(paths);
        const idx = visibleEntries.findIndex((e) => e.path === paths[0]);
        setAnchorIndex(idx >= 0 ? idx : null);
      }
    }
    const origin = rootRef.current?.getBoundingClientRect();
    setCtx({
      x: ev.clientX - (origin?.left ?? 0),
      y: ev.clientY - (origin?.top ?? 0),
      paths: menuPaths,
    });
  };

  /** 選單畫出來後再夾進視窗內，避免估高度估錯、也不要擋到指標。 */
  useLayoutEffect(() => {
    const menu = ctxMenuRef.current;
    const root = rootRef.current;
    if (!menu || !root || !ctx) {
      return;
    }
    const pad = 6;
    const maxX = Math.max(pad, root.clientWidth - menu.offsetWidth - pad);
    const maxY = Math.max(pad, root.clientHeight - menu.offsetHeight - pad);
    const x = Math.min(Math.max(ctx.x, pad), maxX);
    const y = Math.min(Math.max(ctx.y, pad), maxY);
    menu.style.left = `${x}px`;
    menu.style.top = `${y}px`;
  }, [ctx]);

  // ——— 畫面輔助 ———

  const setItemRef = (path: string, el: HTMLElement | null) => {
    if (el) {
      itemRefs.current.set(path, el);
    } else {
      itemRefs.current.delete(path);
    }
  };

  const clipLabel = clipboard
    ? `${clipboard.mode.toUpperCase()} (${clipboard.paths.length})`
    : 'empty';

  const ctxPaths = ctx?.paths?.length ? ctx.paths : selected;
  const ctxEntries = (() => {
    const byPath = new Map<string, DsEntry>();
    // Prefer search / visible list so subtree tag hits keep directory flag.
    visibleEntries.forEach((e) => byPath.set(e.path, e));
    (subtreeHits || []).forEach((e) => byPath.set(e.path, e));
    entries.forEach((e) => {
      if (!byPath.has(e.path)) {
        byPath.set(e.path, e);
      }
    });
    return ctxPaths.map((p) => byPath.get(p)).filter((e): e is DsEntry => !!e);
  })();
  const ctxSingle = ctxEntries.length === 1 ? ctxEntries[0] : null;
  const canPaste = !!(clipboard && clipboard.paths.length > 0);

  type CtxItem = { label?: string; run?: () => void; disabled?: boolean; sep?: boolean };
  const act = (label: string, run: () => void, disabled?: boolean): CtxItem => ({
    label,
    run,
    disabled,
  });
  const ctxItems: CtxItem[] = (() => {
    const n = ctxPaths.length;
    if (n === 0) {
      return [
        act('Paste', () => void doPaste(), !canPaste),
        act('New Folder', () => void doMkdir()),
      ];
    }
    const items: CtxItem[] = [];
    const canAddToPlaylist = ctxEntries.some(
      (e) => e.directory || (!e.directory && isNativeAudioFile(e.name))
    );
    if (ctxSingle) {
      items.push(act('Open', () => openEntry(ctxSingle)));
      if (canAddToPlaylist) {
        items.push(act('Add to playlist', () => addToPlaylist(ctxEntries)));
      }
      items.push(act('Download', () => doDownload(ctxPaths)));
      if (canSetWallpaper(ctxSingle)) {
        items.push(act('Set as wallpaper', () => void doSetWallpaper(ctxSingle.path)));
      }
    } else {
      if (canAddToPlaylist) {
        items.push(act('Add to playlist', () => addToPlaylist(ctxEntries)));
      }
      items.push(act('Download as zip', () => doDownload(ctxPaths)));
      if (ctxEntries.length > 0 && ctxEntries.every((e) => !e.directory)) {
        items.push(act('Download separately', () => doDownloadEach(ctxPaths)));
      }
      items.push(act('Zip', () => void doZip(ctxPaths)));
    }
    const folderPaths = ctxEntries.filter((e) => e.directory).map((e) => e.path);
    if (folderPaths.length > 0) {
      const toAdd = folderPaths.filter((p) => !bookmark.includes(normalizeBookmarkPath(p)));
      items.push(
        act(
          'Add bookmark',
          () => {
            folderPaths.forEach((p) => addBookmark(p));
            setFavOpen(true);
          },
          toAdd.length === 0
        )
      );
    }
    items.push({ sep: true });
    items.push(act('Copy', () => doCopy(ctxPaths)));
    items.push(act('Cut', () => doCut(ctxPaths)));
    items.push(act('Paste', () => void doPaste(), !canPaste));
    if (ctxSingle) {
      items.push(act('Rename', () => void doRename(ctxSingle.path)));
    }
    items.push(act('Delete', () => void doDelete(ctxPaths)));
    items.push({ sep: true });
    items.push(act('Tags…', () => void openTagEditor(ctxPaths)));
    items.push({ sep: true });
    items.push(act('Properties', () => setPropEntries(ctxEntries)));
    return items;
  })();

  useEffect(() => {
    if (!propEntries || propEntries.length === 0) {
      setDu(null);
      setDuLoading(false);
      return;
    }
    const onlyFile = propEntries.length === 1 && !propEntries[0].directory;
    if (onlyFile) {
      setDu({ bytes: propEntries[0].size || 0, files: 1, folders: 0 });
      setDuLoading(false);
      return;
    }
    let cancelled = false;
    setDu(null);
    setDuLoading(true);
    void fileBrowserApi
      .dsDu(propEntries.map((e) => e.path))
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (!res.data.success || !res.data.data) {
          throw new Error(res.data.message || 'Size failed');
        }
        setDu(res.data.data);
      })
      .catch(() => {
        if (!cancelled) {
          setDu(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDuLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [propEntries]);

  return (
    <div
      className={`fb-root${dropHover ? ' fb-drop' : ''}${spaceHeld ? ' fb-space-held' : ''}`}
      ref={rootRef}
      onDragEnter={onRootDragEnter}
      onDragOver={onRootDragOver}
      onDragLeave={onRootDragLeave}
      onDrop={onRootDrop}
    >
      {/* 工具列：導覽、新增資料夾、檢視／排序 */}
      <div className="fb-toolbar">
        <div className="fb-toolbar-group">
          <button
            type="button"
            className="fb-icon-btn"
            onClick={() => goHistory(-1)}
            disabled={historyIndex <= 0 || busy}
            title="Back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button
            type="button"
            className="fb-icon-btn"
            onClick={() => goHistory(1)}
            disabled={historyIndex < 0 || historyIndex >= history.length - 1 || busy}
            title="Forward"
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <button
            type="button"
            className="fb-icon-btn"
            onClick={goHome}
            disabled={busy}
            title={homePath || '/'}
          >
            <FontAwesomeIcon icon={faHouse} />
          </button>
          <button
            type="button"
            className="fb-icon-btn"
            onClick={goUp}
            disabled={!currentPath || !parentPath(currentPath) || busy}
            title="Parent folder"
          >
            <FontAwesomeIcon icon={faArrowUp} />
          </button>
          <button
            type="button"
            className="fb-icon-btn"
            onClick={() => currentPath && refresh(currentPath)}
            onContextMenu={(e) => {
              e.preventDefault();
              if (currentPath && !busy && !loading) {
                void refresh(currentPath, { hardFresh: true });
              }
            }}
            disabled={!currentPath || busy || loading}
            title="Refresh (right-click: hard refresh)"
          >
            <FontAwesomeIcon icon={faRotateRight} />
          </button>
          <button
            type="button"
            className={`fb-icon-btn${favOpen ? ' active' : ''}`}
            onClick={() => {
              setSortOpen(false);
              setIconMenuOpen(false);
              setFavOpen((open) => !open);
            }}
            title="Bookmarks"
            aria-pressed={favOpen}
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
        </div>
        <div className="fb-toolbar-group">
          <button
            type="button"
            className="fb-icon-btn"
            onClick={() => void doMkdir()}
            disabled={busy}
            title="New Folder"
          >
            <FontAwesomeIcon icon={faFolderPlus} />
          </button>
        </div>
        <div className="fb-toolbar-group" style={{ marginLeft: 'auto' }}>
          <div className="fb-sort-wrap" onClick={(ev) => ev.stopPropagation()}>
            <button
              type="button"
              className={`fb-icon-btn${viewMode === 'icons' ? ' active' : ''}`}
              onClick={() => {
                setSortOpen(false);
                patchUserpreference((prev) => ({ ...prev, viewMode: 'icons' }));
                setIconMenuOpen((open) => !(open && viewMode === 'icons'));
              }}
              title="Icons"
            >
              <FontAwesomeIcon icon={faGrip} />
            </button>
            {iconMenuOpen && viewMode === 'icons' && (
              <div className="fb-sort-menu" onMouseLeave={() => setIconMenuOpen(false)}>
                <div className="fb-sort-label">Icon size</div>
                <div className="fb-icon-size-row">
                  <input
                    type="range"
                    min={ICON_SIZE_MIN}
                    max={ICON_SIZE_MAX}
                    step={4}
                    value={iconSize}
                    aria-label="Icon size"
                    onChange={(ev) => {
                      const next = clampIconSize(Number(ev.target.value));
                      patchUserpreference((prev) => ({ ...prev, iconSize: next }));
                    }}
                  />
                  <span className="fb-icon-size-val">{iconSize}</span>
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            className={`fb-icon-btn${viewMode === 'list' ? ' active' : ''}`}
            onClick={() => {
              setIconMenuOpen(false);
              patchUserpreference((prev) => ({ ...prev, viewMode: 'list' }));
            }}
            title="List"
          >
            <FontAwesomeIcon icon={faList} />
          </button>
          <div className="fb-sort-wrap" onClick={(ev) => ev.stopPropagation()}>
            <button
              type="button"
              className={`fb-icon-btn${sortOpen ? ' active' : ''}`}
              onClick={() => {
                setIconMenuOpen(false);
                setSortOpen((open) => !open);
              }}
              title="Sort"
            >
              <FontAwesomeIcon icon={faSort} />
            </button>
            {sortOpen && (
              <div className="fb-sort-menu">
                <button
                  type="button"
                  className={foldersFirst ? 'checked' : ''}
                  onClick={() => patchUserpreference((prev) => ({ ...prev, foldersFirst: !prev.foldersFirst }))}
                >
                  <FontAwesomeIcon icon={faFolder} />
                  <span>Folders first</span>
                  <span className="fb-sort-mark">{foldersFirst ? '✓' : ''}</span>
                </button>
                <div className="fb-ctx-sep" />
                <div className="fb-sort-label">Order by</div>
                {(
                  [
                    ['name', 'File name'],
                    ['size', 'File size'],
                    ['modified', 'Mod date'],
                    ['created', 'Create date'],
                  ] as Array<[SortKey, string]>
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={sortKey === key ? 'checked' : ''}
                    onClick={() => {
                      patchUserpreference((prev) => {
                        if (prev.sortKey === key) {
                          return { ...prev, sortAsc: !prev.sortAsc };
                        }
                        return { ...prev, sortKey: key, sortAsc: key === 'name' };
                      });
                    }}
                  >
                    <span>{label}</span>
                    <span className="fb-sort-mark">
                      {sortKey === key ? (sortAsc ? '↑' : '↓') : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="fb-icon-btn"
            title="File Browser Settings"
            disabled={busy}
            onClick={() => desktopControll?.openFileBrowserSettings?.()}
          >
            <FontAwesomeIcon icon={faGear} />
          </button>
        </div>
      </div>

      {/* 路徑列以下：書籤面板從右側滑入蓋住內容 */}
      <div className="fb-body">
      {/* 路徑列：可輸入後 Enter / Go 跳轉；右側 Filter 開關名稱篩選列 */}
      <div className="fb-pathrow">
        <input
          value={pathDraft}
          onChange={(e) => setPathDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              navigateTo(pathDraft.trim() || '/');
            }
          }}
        />
        <button type="button" onClick={() => navigateTo(pathDraft.trim() || '/')} disabled={busy}>
          Go
        </button>
        <button
          type="button"
          className={`fb-icon-btn${filterOpen ? ' active' : ''}`}
          title="Filter name"
          aria-pressed={filterOpen}
          onClick={toggleFilter}
        >
          <FontAwesomeIcon icon={faFilter} />
        </button>
        <button
          type="button"
          className={`fb-icon-btn${tagFilterOpen || tagFilterTags.length > 0 ? ' active' : ''}`}
          title="Search tags"
          aria-pressed={tagFilterOpen}
          onClick={toggleTagFilter}
        >
          <FontAwesomeIcon icon={faTag} />
        </button>
      </div>

      {filterOpen && (
        <div className="fb-filterrow">
          <input
            ref={filterInputRef}
            value={filterQuery}
            placeholder="Filter name"
            onChange={(e) => onFilterQueryChange(e.target.value)}
          />
        </div>
      )}

      {tagFilterOpen && (
        <div className="fb-filterrow">
          <div className="fb-tag-search-wrap">
            <FontAwesomeIcon icon={faTag} style={{ opacity: 0.45, fontSize: 12 }} />
            {tagFilterTags.map((t) => (
              <span key={t} className="fb-chip">
                <span title={t}>{t}</span>
                <button type="button" title="Remove" onClick={() => removeTagFilter(t)}>
                  ×
                </button>
              </span>
            ))}
            <input
              ref={tagSearchInputRef}
              value={tagSearchDraft}
              placeholder="Search tags…"
              onChange={(e) => {
                setTagSearchDraft(e.target.value);
                setTagAcOpen(true);
                setTagAcIndex(0);
              }}
              onFocus={() => setTagAcOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setTagAcOpen(false), 150);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setTagAcOpen(true);
                  setTagAcIndex((i) => Math.min(i + 1, Math.max(0, tagAcSuggestions.length - 1)));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setTagAcIndex((i) => Math.max(0, i - 1));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  const pick = tagAcSuggestions[tagAcIndex] || tagAcSuggestions[0];
                  if (pick) {
                    addTagFilter(pick);
                  }
                } else if (e.key === 'Escape') {
                  setTagAcOpen(false);
                  if (!tagSearchDraft && tagFilterTags.length === 0) {
                    clearTagFilter();
                  }
                } else if (e.key === 'Backspace' && !tagSearchDraft && tagFilterTags.length > 0) {
                  removeTagFilter(tagFilterTags[tagFilterTags.length - 1]);
                }
              }}
            />
            {tagAcOpen && tagAcSuggestions.length > 0 && (
              <div className="fb-ac">
                {tagAcSuggestions.map((t, i) => (
                  <button
                    key={t}
                    type="button"
                    className={i === tagAcIndex ? 'active' : ''}
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      addTagFilter(t);
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
          <label className="fb-filter-check">
            <input
              type="checkbox"
              checked={tagIncludeSubtree}
              disabled={tagFilterTags.length === 0}
              onChange={(e) => {
                setTagIncludeSubtree(e.target.checked);
                setSelected([]);
                setAnchorIndex(null);
              }}
            />
            Include subfolders
          </label>
        </div>
      )}

      {error && <div className="fb-error">{error}</div>}

      {/* 內容區：只渲染視窗附近的項目（虛擬列表） */}
      <div
        className="fb-viewport"
        ref={viewportRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseDown={onViewportMouseDown}
        onContextMenu={(ev) => openContextMenu(ev, [])}
      >
        {(loading || !bootReady || subtreeLoading) && <div className="fb-empty">Loading…</div>}
        {!loading && bootReady && !subtreeLoading && visibleEntries.length === 0 && (
          <div className="fb-empty">
            {tagIncludeSubtree && tagFilterTags.length > 0
              ? 'No matching tagged items'
              : entries.length === 0
                ? 'This folder is empty'
                : (filterOpen && filterQuery.trim()) || tagFilterTags.length > 0
                  ? 'No matching items'
                  : 'No visible items (hidden files filtered)'}
          </div>
        )}

        {/* 列表模式：表頭固定，列用 gridRow 對應真實索引以維持虛擬捲動位置 */}
        {!loading && bootReady && !subtreeLoading && viewMode === 'list' && visibleEntries.length > 0 && (
          <div
            className="fb-list"
            ref={gridRef}
            style={{
              gridTemplateRows: `var(--fb-list-header-h) repeat(${visibleEntries.length}, var(--fb-list-row-h))`,
            }}
          >
            <div className="fb-list-header" style={{ gridRow: 1 }}>
              <span />
              <span>Name</span>
              <span>Size</span>
              <span>{tagIncludeSubtree && tagFilterTags.length > 0 ? 'Location' : 'Modified'}</span>
              <span>Created</span>
            </div>
            {virtualItems.map(({ entry, index }) => (
              <div
                key={entry.path}
                data-fb-item="1"
                data-fb-path={entry.path}
                ref={(el) => setItemRef(entry.path, el)}
                className={`fb-row${selectedSet.has(entry.path) ? ' selected' : ''}`}
                style={{ gridRow: index + 2 }}
                onClick={(ev) => {
                  ev.stopPropagation();
                  selectClick(entry, index, ev);
                }}
                onDoubleClick={() => openEntry(entry)}
                onContextMenu={(ev) => openContextMenu(ev, [entry.path])}
                onMouseEnter={(ev) => onItemPointerEnter(entry, ev)}
                onMouseLeave={() => onItemPointerLeave(entry)}
              >
                <img
                  className={entryIconClass(entry)}
                  src={entryIconSrc(entry)}
                  alt=""
                  draggable={false}
                />
                <div className="name" title={entry.path}>
                  <span className="fb-name-text">{entry.name}</span>
                  {tagFilterOpen && entryTags(entry).length > 0 && (
                    <span className="fb-name-tags">
                      {entryTags(entry).slice(0, 2).map((t) => (
                        <span key={t} className="fb-chip fb-chip-muted" title={t}>
                          <span>{t}</span>
                        </span>
                      ))}
                      {entryTags(entry).length > 2 && (
                        <span className="fb-chip-more">+{entryTags(entry).length - 2}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="meta">{formatSize(entry.size, entry.directory)}</div>
                {tagIncludeSubtree && tagFilterTags.length > 0 ? (
                  <div className="meta fb-hit-parent" title={parentPath(entry.path) || '/'}>
                    {parentPath(entry.path) || '/'}
                  </div>
                ) : (
                  <div className="meta" title={formatDate(entry.lastModifiedEpochMs)}>
                    {formatDate(entry.lastModifiedEpochMs)}
                  </div>
                )}
                <div className="meta" title={formatDate(entry.createdEpochMs)}>
                  {formatDate(entry.createdEpochMs)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 圖示模式：--fb-ico-size 來自偏好；skip 列撐起上方未渲染的空間 */}
        {!loading && bootReady && !subtreeLoading && viewMode === 'icons' && visibleEntries.length > 0 && (
          <div
            className="fb-icons"
            ref={gridRef}
            style={{
              ['--fb-ico-size' as string]: `${iconSize}px`,
              ['--fb-cell-min-w' as string]: `${iconSize + 40}px`,
              ['--fb-cell-h' as string]: `${iconSize + 54}px`,
              gridTemplateRows: `repeat(${Math.max(iconRows, 1)}, var(--fb-cell-h))`,
            }}
          >
            {iconStartRow > 0 && (
              <div
                aria-hidden
                className="fb-icons-skip"
                style={{ gridColumn: '1 / -1', gridRow: `1 / ${iconStartRow + 1}` }}
              />
            )}
            {virtualItems.map(({ entry, index }) => (
              <div
                key={entry.path}
                data-fb-item="1"
                data-fb-path={entry.path}
                ref={(el) => setItemRef(entry.path, el)}
                className={`fb-icon-item${selectedSet.has(entry.path) ? ' selected' : ''}${
                  thumbPeek?.path === entry.path ? ' peeking' : ''
                }`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  selectClick(entry, index, ev);
                }}
                onDoubleClick={() => openEntry(entry)}
                onContextMenu={(ev) => openContextMenu(ev, [entry.path])}
                onMouseEnter={(ev) => onItemPointerEnter(entry, ev)}
                onMouseLeave={() => onItemPointerLeave(entry)}
              >
                <img
                  className={entryIconClass(entry)}
                  src={entryIconSrc(entry)}
                  alt=""
                  draggable={false}
                />
                {tagFilterOpen && entryTags(entry).length > 0 && (
                  <span className="fb-tag-badge" title={entryTags(entry).join(', ')}>
                    {entryTags(entry).length}
                  </span>
                )}
                <div className="name" title={entry.path}>
                  {entry.name}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fb-status">
        <span>{currentPath ?? '…'}</span>
        <span>
          {visibleEntries.length} items · {selected.length} selected · clipboard: {clipLabel}
          {statusNote ? ` · ${statusNote}` : ''}
          {busy ? ` · ${busyMsg || 'working…'}` : ''}
        </span>
      </div>

      <div
        className={`fb-fav-backdrop${favOpen ? ' open' : ''}`}
        onClick={() => setFavOpen(false)}
      />
      <aside
        className={`fb-fav-pane${favOpen ? ' open' : ''}`}
        aria-hidden={!favOpen}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="fb-fav-add"
          disabled={!currentPath || busy || !!(currentPath && bookmark.includes(normalizeBookmarkPath(currentPath)))}
          onClick={() => currentPath && addBookmark(currentPath)}
        >
          Add current path to bookmark
        </button>
        <hr className="fb-fav-hr" />
        <div className="fb-fav-list">
          {bookmark.length === 0 && <div className="fb-fav-empty">No bookmarks</div>}
          {bookmark.map((path) => (
            <div key={path} className="fb-fav-item">
              <button
                type="button"
                className="fb-fav-go"
                title={path}
                onClick={() => navigateTo(path)}
              >
                <img src={resolveEntryIcon(bookmarkLabel(path), true)} alt="" draggable={false} />
                <span>{bookmarkLabel(path)}</span>
              </button>
              <button
                type="button"
                className="fb-fav-remove"
                title="Remove"
                onClick={() => removeBookmark(path)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ))}
        </div>
      </aside>
      </div>

      {/* 框選矩形（畫面座標，已裁到視窗內） */}
      {marquee && (
        <div
          className="fb-marquee"
          style={{
            left: marquee.x,
            top: marquee.y,
            width: marquee.w,
            height: marquee.h,
          }}
        />
      )}

      {/* 右鍵選單（相對視窗本體，座標對齊指標） */}
      {ctx && (
        <div
          ref={ctxMenuRef}
          className="fb-ctx"
          style={{ left: ctx.x, top: ctx.y }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {ctxItems.map((it, i) =>
            it.sep ? (
              <div key={`sep-${i}`} className="fb-ctx-sep" />
            ) : (
              <button
                key={it.label}
                type="button"
                disabled={it.disabled || busy}
                onClick={() => {
                  setCtx(null);
                  it.run?.();
                }}
              >
                {it.label}
              </button>
            )
          )}
        </div>
      )}

      {propEntries && (
        <div
          ref={propRef}
          className="fb-prop"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="fb-prop-head">
            <strong>Properties</strong>
            <button type="button" onClick={() => setPropEntries(null)}>
              Close
            </button>
          </div>
          {propEntries.length > 1 && (
            <div className="fb-prop-row">
              <span>Items</span>
              <span>{propEntries.length}</span>
            </div>
          )}
          <div className="fb-prop-row">
            <span>{propEntries.length > 1 ? 'Total size' : 'Size'}</span>
            <span>
              {duLoading ? 'Calculating…' : du ? formatSize(du.bytes, false) : '—'}
            </span>
          </div>
          {(duLoading || du) && (propEntries.length > 1 || propEntries.some((e) => e.directory)) && (
            <div className="fb-prop-row">
              <span>Contains</span>
              <span>
                {duLoading
                  ? 'Calculating…'
                  : `${du?.files ?? 0} files, ${du?.folders ?? 0} folders`}
              </span>
            </div>
          )}
          {(propEntries.length === 1 ? propEntries : propEntries.slice(0, 8)).map((e) => (
            <div key={e.path} className="fb-prop-block">
              <div className="fb-prop-row">
                <span>Name</span>
                <span title={e.path}>{e.name}</span>
              </div>
              {propEntries.length === 1 && (
                <>
                  <div className="fb-prop-row">
                    <span>Type</span>
                    <span>{e.directory ? 'Folder' : 'File'}</span>
                  </div>
                  <div className="fb-prop-row">
                    <span>Path</span>
                    <span title={e.path}>{e.path}</span>
                  </div>
                  <div className="fb-prop-row">
                    <span>Modified</span>
                    <span>{formatDate(e.lastModifiedEpochMs)}</span>
                  </div>
                  <div className="fb-prop-row">
                    <span>Created</span>
                    <span>{formatDate(e.createdEpochMs)}</span>
                  </div>
                  {(e.owner || e.group) && (
                    <div className="fb-prop-row">
                      <span>Owner</span>
                      <span>{[e.owner, e.group].filter(Boolean).join(' / ')}</span>
                    </div>
                  )}
                  <div className="fb-prop-row">
                    <span>Tags</span>
                    <div className="fb-prop-tags">
                      {entryTags(e).length === 0 && <span className="fb-prop-muted">None</span>}
                      {entryTags(e).map((t) => (
                        <span key={t} className="fb-chip">
                          <span title={t}>{t}</span>
                          <button
                            type="button"
                            title="Remove"
                            disabled={busy}
                            onClick={() => void applyRemoveTags([e.path], [t])}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <button
                        type="button"
                        className="fb-chip fb-chip-muted"
                        disabled={busy}
                        onClick={() => void openTagEditor([e.path])}
                      >
                        Edit…
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
          {propEntries.length > 8 && (
            <div className="fb-prop-muted">+{propEntries.length - 8} more</div>
          )}
          {propEntries.length > 1 && (
            <div className="fb-prop-row" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="fb-chip fb-chip-muted"
                disabled={busy}
                onClick={() => void openTagEditor(propEntries.map((p) => p.path))}
              >
                Edit tags…
              </button>
            </div>
          )}
        </div>
      )}

      {tagEditor && (
        <div
          ref={tagEditorRef}
          className="fb-tag-editor"
          style={{ left: 24, bottom: 48 }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="fb-tag-editor-head">
            <strong>
              Tags
              {tagEditor.paths.length > 1 ? ` (${tagEditor.paths.length} items)` : ''}
            </strong>
            <button type="button" onClick={() => setTagEditor(null)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="fb-tag-editor-list">
            {tagEditor.current.length === 0 && (
              <span className="fb-tag-editor-hint">No tags yet</span>
            )}
            {tagEditor.current.map((t) => (
              <span key={t} className="fb-chip on">
                <span title={t}>{t}</span>
                <button
                  type="button"
                  title="Remove"
                  disabled={busy}
                  onClick={() => void applyRemoveTags(tagEditor.paths, [t], { keepEditor: true })}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            className="fb-tag-editor-input"
            value={tagEditor.draft}
            placeholder="Add a tag…"
            autoFocus
            disabled={busy}
            onChange={(e) => setTagEditor((prev) => (prev ? { ...prev, draft: e.target.value } : prev))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const t = tagEditor.draft.trim();
                if (!t || tagEditor.current.includes(t)) {
                  setTagEditor((prev) => (prev ? { ...prev, draft: '' } : prev));
                  return;
                }
                void applyAddTags(tagEditor.paths, [t], { keepEditor: true });
              } else if (e.key === 'Escape') {
                setTagEditor(null);
              }
            }}
          />
          {tagEditor.suggestions.length > 0 && (
            <>
              <div className="fb-tag-editor-hint" style={{ marginBottom: 4 }}>
                Suggestions
              </div>
              <div className="fb-tag-editor-list">
                {tagEditor.suggestions.slice(0, 24).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="fb-chip fb-chip-muted"
                    disabled={busy}
                    onClick={() => void applyAddTags(tagEditor.paths, [t], { keepEditor: true })}
                  >
                    <span>{t}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="fb-tag-editor-hint">Enter to add · × to remove</div>
        </div>
      )}

      {busy && (
        <div className="fb-busy">
          <div className="fb-busy-card">{busyMsg || 'Working…'}</div>
        </div>
      )}

      {/* 空白鍵 + hover：有縮圖的項目在原位置浮起放大 3.5× */}
      {folderTagTip && (
        <div
          className="fb-folder-tag-tip"
          style={{
            left: folderTagTip.left,
            top: folderTagTip.top,
            width: folderTagTip.width,
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {folderTagTip.tags.map((t) => (
            <span key={t} className="fb-chip">
              <span>{t}</span>
            </span>
          ))}
        </div>
      )}

      {thumbPeek && (
        <div
          className="fb-thumb-peek"
          style={{
            left: thumbPeek.left,
            top: thumbPeek.top,
            width: thumbPeek.width,
            height: thumbPeek.height,
          }}
        >
          <img src={thumbPeek.src} alt="" draggable={false} />
        </div>
      )}
    </div>
  );
}

/** 桌面載入小程式時讀 config + 這個 React 本體。 */
const App: AppObj = {
  config: fileBrowserConfig,
  body: FileBrowser,
};

export default App;
